import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createApplicationNote,
	GetLatestNoteOfSpecificApplication,
	GetAllNotesOfSpecificApplication
} from "../api/application-notes-api";
import { ApplicationNote } from "../models/ApplicationNote";
import { AddApplicationNoteRequest } from "../models/requests/AddApplicationNoteRequest";
import { useLinearProgressContext } from "../contexts/useLinearProgressContext";
import { useSnackbarContext } from "../contexts/useSnackBarContext";

/**
 * Hook to get the latest application note of a specific application
 * @param freelancerOnboardingId The ID of the freelancer onboarding
 * @param userId The user ID to include in the header
 * @param isEnabled Whether the query should be enabled
 */
export const useLatestNoteOfSpecificApplication = (
	freelancerOnboardingId: number,
	isEnabled: boolean = true
) => {
	return useQuery<ApplicationNote, Error>({
		queryKey: ["notes", "latest", freelancerOnboardingId],
		queryFn: () => GetLatestNoteOfSpecificApplication(freelancerOnboardingId),
		enabled: isEnabled && freelancerOnboardingId > 0,
		refetchOnMount: "always",
		refetchOnWindowFocus: false,
		gcTime: 0,
		staleTime: 0
	});
};

/**
 * Hook to get all application notes of a specific application
 * @param freelancerOnboardingId The ID of the freelancer onboarding
 * @param userId The user ID to include in the header
 */
export const useAllNotesOfSpecificApplication = (freelancerOnboardingId: number) => {
	return useQuery<ApplicationNote[], Error>({
		queryKey: ["notes", "all", freelancerOnboardingId],
		queryFn: () => GetAllNotesOfSpecificApplication(freelancerOnboardingId),
		refetchOnMount: "always",
		refetchOnWindowFocus: false,
		gcTime: 0,
		staleTime: 0
	});
};

/**
 * Hook to create a new application note
 */
export const useCreateApplicationNote = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useSnackbarContext();
	const { showProgress, hideProgress } = useLinearProgressContext();

	return useMutation({
		mutationFn: ({
			noteRequest
		}: {
			noteRequest: AddApplicationNoteRequest;
			userId: number;
		}) => {
			showProgress();
			return createApplicationNote(noteRequest);
		},
		onSuccess: (newNote, { noteRequest }) => {
			hideProgress();
			showSuccess("note.createSuccess");

			queryClient.setQueryData(
				["notes", "latest", noteRequest.freelancerOnboardingId],
				newNote
			);

			queryClient.invalidateQueries({
				queryKey: ["notes", "all", noteRequest.freelancerOnboardingId]
			});
		},
		onError: (error: Error) => {
			hideProgress();
			console.error("Failed to create note:", error);
			showError("note.createError");
		}
	});
};
